import { User, UserRole } from "../../../../users/userTypes"
import { falsyToUndefined } from "../../../../../lib/lang"

import { CompanyUserFormMode, CompanyUserFormValues } from "./companyUserFormTypes"
import { CompanyUserPayload, SaveCompanyUserBody, UserAction, UserActionType } from "./usersApi"

/**
 * Seeds the create/edit form from an existing user (or defaults for create). Email invitation defaults to
 * on for create and off for edit, so a routine save never re-sends an invitation.
 */
export function getCompanyUserInitialValues(user?: User): CompanyUserFormValues {
    return {
        employeeId: user?.employeeId ?? "",
        displayName: user?.displayName ?? "",
        initials: user?.initials ?? "",
        username: user?.username ?? "",
        email: user?.email ?? "",
        mobileNumber: user?.mobileNumber ?? "",
        phoneNumber: user?.phoneNumber ?? "",
        language: user?.language ?? "",
        userGroupId: user?.userGroup ? String(user.userGroup.id) : "",
        departmentId: user?.department ? String(user.department.id) : "",
        approverForUserGroupIds: user?.approverForUserGroups?.map((group) => group.id) ?? [],
        assignedPriceListIds: user?.assignedPriceLists?.map((priceList) => priceList.id) ?? [],
        sendEmailInvitation: user === undefined,
        disableUserSync: user?.company ? !user.company.companySetup.userSynchronization : false,
        isSecretary: user?.roles?.includes(UserRole.SECRETARY) ?? false,
        notifyOnGuestArrival: user?.notificationSettings?.notifyOnGuestArrival ?? false,
        isActive: user?.isActive ?? true,
        workgroups: user?.workgroups ?? [],
    }
}

/**
 * Maps form values to the POST /users(/:id) request body under the updated contract: user-sync lives under
 * `company.companySetup.userSynchronization` (inverse of the "Disable user sync" toggle), the secretary role
 * goes into `roles`, and the email invitation is emitted as a sibling `actions` entry.
 */
export function buildSaveCompanyUserBody(
    values: CompanyUserFormValues,
    mode: CompanyUserFormMode,
): SaveCompanyUserBody {
    const user: CompanyUserPayload = {
        employeeId: falsyToUndefined(values.employeeId),
        initials: falsyToUndefined(values.initials),
        username: falsyToUndefined(values.username),
        displayName: values.displayName,
        email: values.email,
        phoneNumber: falsyToUndefined(values.phoneNumber),
        mobileNumber: falsyToUndefined(values.mobileNumber),
        language: falsyToUndefined(values.language),
        roles: values.isSecretary ? [UserRole.SECRETARY] : [],
        company: { companySetup: { userSynchronization: !values.disableUserSync } },
        userGroup: values.userGroupId ? { id: Number(values.userGroupId) } : undefined,
        department: values.departmentId ? { id: Number(values.departmentId) } : undefined,
        workgroups: values.workgroups.map((workgroup) => ({ id: workgroup.id })),
        approverForUserGroups: values.approverForUserGroupIds.map((id) => ({ id })),
        assignedPriceLists: values.assignedPriceListIds.map((id) => ({ id })),
        notificationSettings: { notifyOnGuestArrival: values.notifyOnGuestArrival },
        ...(mode === "update" ? { isActive: values.isActive } : {}),
    }

    const actions: ReadonlyArray<UserAction> = values.sendEmailInvitation
        ? [{ type: UserActionType.SEND_EMAIL_INVITATION }]
        : []

    return { user, actions }
}
